
import React from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Trip } from '@/types/itinerary';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface TripDeleteDialogProps {
  trip: Trip & { isJoined?: boolean };
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onDelete?: () => void;
}

const TripDeleteDialog: React.FC<TripDeleteDialogProps> = ({
  trip,
  isOpen,
  setIsOpen,
  onDelete,
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDeleteTrip = async () => {
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      console.log(`${trip.isJoined ? "Leaving" : "Deleting"} trip:`, trip.id);
      
      if (trip.isJoined) {
        // If user is just a member (not owner), remove themselves from trip
        const user = (await supabase.auth.getUser()).data.user;
        
        if (!user) {
          throw new Error("User not authenticated");
        }
        
        const { error } = await supabase
          .from('trip_members')
          .delete()
          .eq('trip_id', trip.id)
          .eq('user_id', user.id);
          
        if (error) {
          console.error('Supabase error details:', error);
          throw error;
        }
        
        toast.success('Successfully left the trip');
      } else {
        // If user is the owner, delete the entire trip
        const { error } = await supabase
          .from('trips')
          .delete()
          .eq('id', trip.id);
          
        if (error) {
          console.error('Supabase error details:', error);
          throw error;
        }
        
        toast.success('Trip deleted successfully');
      }
      
      // Close dialog first
      setIsOpen(false);
      
      // Call the onDelete callback immediately without any delay
      if (onDelete) {
        console.log("Calling onDelete callback immediately");
        onDelete();
      }
    } catch (error) {
      console.error('Error during trip operation:', error);
      toast.error(trip.isJoined ? 
        'Failed to leave the trip. Please try again.' : 
        'Failed to delete trip. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {trip.isJoined ? "Leave Trip" : "Delete Trip"}
          </DialogTitle>
          <DialogDescription>
            {trip.isJoined 
              ? "Are you sure you want to leave this trip? You'll need to be invited again to rejoin."
              : "Are you sure you want to delete this trip? This action cannot be undone and all data will be permanently lost."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteTrip}
            disabled={isDeleting}
            className="bg-red-500 hover:bg-red-600"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span>
                  {trip.isJoined ? "Leaving..." : "Deleting..."}
                </span>
              </>
            ) : (
              trip.isJoined ? "Leave Trip" : "Delete Trip"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TripDeleteDialog;
